<script lang="ts">
import type { Transaction } from '../../lib/types';
import { Button, Modal } from '../common';
import { getCategories } from '../../lib/storage';
import * as categoryOperations from '../../lib/storage/operations/categoryOperations';
import { onMount } from 'svelte';
import MerchantGroupCard from './MerchantGroupCard.svelte';

type Props = {
  isOpen: boolean;
  mediumConfidenceTransactions: Transaction[];
  uncategorizedTransactions: Transaction[];
  allTransactions: Transaction[]; // Pass all transactions from parent
  onClose: () => void;
  onComplete: (decisions: ReviewDecision[]) => void;
};

type ReviewDecision = {
  transactionId: string;
  description: string;
  originalCategory: string;
  confirmedCategory: string;
  confidence: number;
  createRule: boolean;
  action: 'confirm' | 'change' | 'skip';
  groupedIds?: string[]; // IDs of all transactions in this group
};

type GroupReview = {
  transaction: Transaction; // Representative transaction with _groupCount and _groupedIds
  action: 'confirm' | 'skip';
  selectedCategory: string;
  createRule: boolean;
};

let {
  isOpen = $bindable(false),
  mediumConfidenceTransactions = [],
  uncategorizedTransactions = [],
  allTransactions = [],
  onClose,
  onComplete
}: Props = $props();

// Grouped transactions (already grouped by backend with _groupCount and _groupedIds)
let groupedTransactions = $derived([...mediumConfidenceTransactions, ...uncategorizedTransactions]);

// Create a fast lookup map for transactions by ID (computed once)
function createTransactionLookup() {
  const map = new Map<string, Transaction>();
  for (const txn of allTransactions) {
    map.set(txn.id, txn);
  }
  return map;
}

let transactionLookup = $derived(createTransactionLookup());

// Track review state for each merchant group (by representative transaction ID)
let groupReviews = $state<Map<string, GroupReview>>(new Map());

// Select all state
let createRuleForAll = $state(true);

// Track if modal was previously open to detect transitions
let wasOpen = $state(false);

// Categories loaded from API
let categories = $state<string[]>([]);

// Load categories on mount
onMount(async () => {
  categories = await getCategories();
});

// Initialize reviews when modal opens (only on transition from closed to open)
$effect(() => {
  if (isOpen && !wasOpen && groupedTransactions.length > 0) {
    console.log('Modal opened! Grouped transactions:', groupedTransactions);
    const newReviews = new Map<string, GroupReview>();

    groupedTransactions.forEach(transaction => {
      newReviews.set(transaction.id, {
        transaction,
        action: 'confirm',
        selectedCategory: transaction.category,
        createRule: true
      });
    });

    groupReviews = newReviews;
    console.log('Initialized reviews for', groupReviews.size, 'merchant groups');
  }

  // Track previous state
  wasOpen = isOpen;
});

// Computed values
let reviewedCount = $derived(
  Array.from(groupReviews.values()).filter(r => r.action === 'confirm').length
);
let skippedCount = $derived(
  Array.from(groupReviews.values()).filter(r => r.action === 'skip').length
);

// Group action handlers
function toggleGroupAction(groupId: string) {
  const review = groupReviews.get(groupId);
  if (!review) return;

  const newReviews = new Map(groupReviews);
  newReviews.set(groupId, {
    ...review,
    action: review.action === 'confirm' ? 'skip' : 'confirm'
  });
  groupReviews = newReviews;
}

function toggleGroupCreateRule(groupId: string) {
  const review = groupReviews.get(groupId);
  if (!review) return;

  const newReviews = new Map(groupReviews);
  newReviews.set(groupId, {
    ...review,
    createRule: !review.createRule
  });
  groupReviews = newReviews;
}

function handleGroupCategoryChange(groupId: string, category: string) {
  const review = groupReviews.get(groupId);
  if (!review) return;

  const newReviews = new Map(groupReviews);
  newReviews.set(groupId, {
    ...review,
    selectedCategory: category
  });
  groupReviews = newReviews;
}

function handleSelectAllConfirm() {
  const newReviews = new Map(groupReviews);
  groupReviews.forEach((review, id) => {
    newReviews.set(id, {
      ...review,
      action: 'confirm'
    });
  });
  groupReviews = newReviews;
}

function handleSelectAllSkip() {
  const newReviews = new Map(groupReviews);
  groupReviews.forEach((review, id) => {
    newReviews.set(id, {
      ...review,
      action: 'skip'
    });
  });
  groupReviews = newReviews;
}

function handleToggleRuleForAll() {
  const newReviews = new Map(groupReviews);
  groupReviews.forEach((review, id) => {
    newReviews.set(id, {
      ...review,
      createRule: createRuleForAll
    });
  });
  groupReviews = newReviews;
}

async function handleCreateCategory(categoryName: string) {
  const result = await categoryOperations.add(categoryName);
  if (result.success && result.categories) {
    categories = result.categories;
  } else {
    console.error('Failed to create category:', result.error);
    throw new Error(result.error || 'Failed to create category');
  }
}

function handleApply() {
  console.log('handleApply called, groupReviews:', groupReviews);

  const decisions: ReviewDecision[] = [];

  groupReviews.forEach(review => {
    const transaction = review.transaction;
    const decision: ReviewDecision = {
      transactionId: transaction.id,
      // Use displayName for rules if available, otherwise fall back to description
      description: transaction.displayName || transaction.description,
      originalCategory: transaction.category,
      confirmedCategory: review.selectedCategory,
      confidence: transaction.confidence || 0,
      createRule: review.createRule && review.action === 'confirm',
      action: review.action === 'confirm' ?
        (review.selectedCategory !== transaction.category ? 'change' : 'confirm') :
        'skip',
      groupedIds: transaction._groupedIds // Include all grouped transaction IDs
    };
    decisions.push(decision);
  });

  console.log('Created decisions:', decisions);
  console.log('Calling onComplete');
  onComplete(decisions);
}

function handleCancel() {
  onClose();
}
</script>

{#if isOpen}
<Modal {isOpen} {onClose} title="Review Categorizations" size="xl">
  {#snippet children()}
  <div class="review-container">
    <!-- Header with stats -->
    <div class="header-section">
      <p class="subtitle">
        Review {groupedTransactions.length} grouped transaction{groupedTransactions.length === 1 ? '' : 's'}.
        Transactions are grouped by merchant name. <strong>Confirm</strong> to accept/change the category and apply to all similar transactions. <strong>Skip</strong> to review later.
      </p>
      <div class="stats">
        <span class="stat confirm">✓ Confirming: {reviewedCount}</span>
        <span class="stat skip">→ Skipping for now: {skippedCount}</span>
      </div>
    </div>

    <!-- Bulk actions -->
    <div class="actions-bar">
      <div class="action-buttons-left">
        <Button variant="secondary" size="sm" onclick={handleSelectAllConfirm}>
          {#snippet children()}Select All Confirm{/snippet}
        </Button>
        <Button variant="secondary" size="sm" onclick={handleSelectAllSkip}>
          {#snippet children()}Select All Skip{/snippet}
        </Button>
      </div>

      <label class="create-rule-all">
        <input type="checkbox" bind:checked={createRuleForAll} onchange={handleToggleRuleForAll} />
        <span>Create rules for confirmed items</span>
      </label>
    </div>

    <!-- Merchant Groups -->
    {#if groupedTransactions.length > 0}
      <div class="groups-section">
        <h3 class="section-title">Merchant Groups ({groupedTransactions.length})</h3>
        <p class="section-description">These transactions are grouped by merchant. Review each group to confirm or change the category for all similar transactions.</p>

        <div class="merchant-groups">
          <!-- Header Row -->
          <div class="groups-header">
            <div class="header-checkbox">✓</div>
            <div class="header-merchant">Merchant</div>
            <div class="header-date">Date Range</div>
            <div class="header-count">Count</div>
            <div class="header-amount">Amount</div>
            <div class="header-category">Category</div>
            <div class="header-rule">Rule</div>
          </div>

          {#each groupedTransactions as transaction (transaction.id)}
            {@const review = groupReviews.get(transaction.id)}
            {#if review}
              <MerchantGroupCard
                transaction={review.transaction}
                transactionLookup={transactionLookup}
                {categories}
                selectedCategory={review.selectedCategory}
                createRule={review.createRule}
                action={review.action}
                onToggleAction={() => toggleGroupAction(transaction.id)}
                onToggleCreateRule={() => toggleGroupCreateRule(transaction.id)}
                onCategoryChange={(category) => handleGroupCategoryChange(transaction.id, category)}
                onCreateCategory={handleCreateCategory}
              />
            {/if}
          {/each}
        </div>
      </div>
    {/if}

    <!-- Bottom action buttons -->
    <div class="bottom-actions">
      <Button variant="ghost" onclick={handleCancel}>
        {#snippet children()}Cancel{/snippet}
      </Button>
      <Button variant="primary" onclick={handleApply}>
        {#snippet children()}Save Changes ({reviewedCount} to confirm, {skippedCount} to review later){/snippet}
      </Button>
    </div>
  </div>
  {/snippet}
</Modal>
{/if}

<style lang="scss">
.review-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  padding: var(--spacing-md);
}

.header-section {
  .subtitle {
    margin: 0 0 var(--spacing-md) 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
  }

  .stats {
    display: flex;
    gap: var(--spacing-lg);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);

    .stat {
      &.confirm {
        color: var(--color-success);
      }
      &.skip {
        color: var(--color-text-secondary);
      }
    }
  }
}

.actions-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md);
  background: var(--color-background-secondary);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);

  .action-buttons-left {
    display: flex;
    gap: var(--spacing-sm);
  }

  .create-rule-all {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    cursor: pointer;
    user-select: none;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);

    input[type="checkbox"] {
      cursor: pointer;
    }
  }
}

.groups-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);

  .section-title {
    margin: 0;
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-primary);
  }

  .section-description {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-secondary);
    line-height: var(--line-height-normal);
  }
}

.merchant-groups {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-background);
  overflow: hidden;
}

.groups-header {
  display: grid;
  grid-template-columns: 40px minmax(200px, 2fr) 120px 80px 100px minmax(160px, 1fr) 50px;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-background-secondary);
  border-bottom: 2px solid var(--color-border);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: sticky;
  top: 0;
  z-index: 1;

  .header-checkbox {
    text-align: center;
  }

  .header-merchant {
    min-width: 0;
  }

  .header-count {
    text-align: center;
  }

  .header-amount {
    text-align: right;
  }

  .header-rule {
    text-align: center;
  }
}

.bottom-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: var(--spacing-md);
  border-top: 1px solid var(--color-border);
}
</style>
